// @ts-strict-ignore
import { useFlippers } from "@shared/context/FlippersContext";
import type { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import {
  FilterSource,
  useAnalyticsLogger,
} from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useCatalogTypeVar } from "@shared/reactiveVar/useCatalogType/useCatalogType.store";
import { setSearchAnalyticToFilter } from "@shared/reactiveVar/useSearchAnalytics/useSearchAnalytics.store";

import { ClinicFiltersContent } from "./ClinicFiltersContent";
import { EmersonFiltersContent } from "./EmersonFiltersContent";
import { PatientFiltersContent } from "./PatientFiltersContent";

interface Props {
  entryPointForLogging?: SearchApplyEntryPoint;
  eventNameForLogging?: AnalyticsEvent;
  isDrawer?: boolean;
  isBold?: boolean;
  isCyberMondayEnabled?: boolean;
}

const FiltersContent = ({
  isDrawer = false,
  isBold = false,
  isCyberMondayEnabled = false,
  entryPointForLogging,
  eventNameForLogging,
}: Props) => {
  const logAnalyticsEvent = useAnalyticsLogger();
  const { isUnauthenticated, isClinic, isEmerson } = useSharedGlobalConfig();
  const { type } = useCatalogTypeVar();
  const [isSpotlightCampaignFlipperEnabled] = useFlippers(
    "dora_condition_specific_spotlight_campaign"
  );
  const [isCategoryPagesFlipperEnabled] = useFlippers("dora_category_pages");
  const entryPoint =
    type === "Wholesale" ? SearchApplyEntryPoint.WHOLESALE_CATALOG_PAGE : entryPointForLogging;
  const filterSource =
    isSpotlightCampaignFlipperEnabled || isCategoryPagesFlipperEnabled
      ? FilterSource.LEFT_PANEL
      : null;

  const logSearchApplyFilterEvent = (entityName, entityType) => {
    if (!entryPointForLogging) return;
    if (isUnauthenticated) return;

    logAnalyticsEvent({
      eventName: eventNameForLogging,
      payload: {
        entry_point: entryPoint,
        content: entityName,
        filter_type: entityType,
        search_version: 2,
      },
    });
  };

  const recordFullscriptFilterEvent = (entityName, entityType) => {
    setSearchAnalyticToFilter(
      eventNameForLogging,
      entryPoint,
      entityName,
      entityType,
      filterSource
    );
  };

  if (isEmerson) {
    return <EmersonFiltersContent logSearchApplyFilterEvent={logSearchApplyFilterEvent} />;
  }

  if (isClinic) {
    return (
      <ClinicFiltersContent
        isBold={isBold}
        logSearchApplyFilterEvent={recordFullscriptFilterEvent}
        isDrawer={isDrawer}
        isCyberMondayEnabled={isCyberMondayEnabled}
      />
    );
  }

  return (
    <PatientFiltersContent
      isBold={isBold}
      logSearchApplyFilterEvent={recordFullscriptFilterEvent}
      isDrawer={isDrawer}
      isCyberMondayEnabled={isCyberMondayEnabled}
    />
  );
};

export { FiltersContent };
